import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { api, clearSession, getUser, setSession, type SessionUser } from './api/client';

type LoginResult = { success: boolean; token: string; user: SessionUser };

type RegisterInput = { companyName: string; name: string; email: string; password: string };

type Session = {
  user: SessionUser | null;
  signIn: (email: string, password: string) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  signOut: () => void;
};

const Ctx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(getUser());

  const signIn = useCallback(async (email: string, password: string) => {
    // web-login, not login: the console's door refuses anyone who is not a super
    // admin or an owner who has been granted access, without touching the 28
    // routes the Android app calls every day (§4.2).
    const res = await api<LoginResult>('/api/users/web-login', {
      method: 'POST', body: { email, password },
    });
    setSession(res.token, res.user);
    setUser(res.user);
  }, []);

  const register = useCallback(async (input: RegisterInput) => {
    const res = await api<LoginResult & { message?: string }>('/api/users/register-company', {
      method: 'POST', body: input,
    });

    // register-company mints an ordinary 30-day app token. A browser session is
    // meant to last 12 hours (§4.3), so trade it immediately for a web one —
    // this is the only moment the password is in hand to do so. If that call
    // fails for any reason, the registration still succeeded and the longer
    // token is better than dropping the user back at a login screen.
    try {
      const web = await api<LoginResult>('/api/users/web-login', {
        method: 'POST', body: { email: input.email, password: input.password },
      });
      setSession(web.token, web.user);
      setUser(web.user);
      return;
    } catch {
      setSession(res.token, res.user);
      setUser(res.user);
    }
  }, []);

  const signOut = useCallback(() => {
    // Server-side revocation is a separate call the app already has; here the
    // 12-hour token simply stops being held.
    clearSession();
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, signIn, register, signOut }), [user, signIn, register, signOut]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
